import { Role, type GameState } from '@susdle/shared';
import type { MeetingView, MeetingPlayer, Pov, SecretWordTruth } from '../ghost-meeting/types.js';
import { classifyAvatarUrl } from './stateToView.js';

export function povFromRole(role: Role): Pov {
  if (role === Role.Ghost) return 'ghost';
  if (role === Role.Fool) return 'fool';
  return 'villager';
}

export interface MeetingMappingContext {
  meId: string;
}

/**
 * Maps a ghost-meeting `GameState` to the local viewer's `MeetingView`.
 *
 * POV stripping mirrors the server's `@view` filtering: `role`/`secretWord`/
 * `secretWordLength` are SELF-tagged (only the local player's own decode);
 * `ghostIds`/`ghostMeetingVotes` are GHOSTS-tagged. On real (already-filtered)
 * state a non-Ghost simply has empty ghost fields; the explicit `isGhost` gate
 * makes the dev builder's UNFILTERED state behave identically so one generated
 * state can be rendered from any POV.
 */
export function stateToMeetingView(state: GameState, ctx: MeetingMappingContext): MeetingView {
  const me = state.players.get(ctx.meId);
  const role = me?.role ?? Role.Villager;
  const isGhost = role === Role.Ghost;

  const players: MeetingPlayer[] = [];
  for (const [, player] of state.players) {
    if (!player.alive) continue; // eliminated players are out of the meeting cast
    const avatar = classifyAvatarUrl(player.avatarUrl);
    const meetingPlayer: MeetingPlayer = {
      id: player.id,
      name: player.name,
      avatarUrl: player.avatarUrl,
      isDefaultAvatar: avatar.isDefaultAvatar,
      turnOrder: player.turnOrder,
      connected: player.connected,
    };
    if (avatar.defaultAvatarIndex !== undefined) {
      meetingPlayer.defaultAvatarIndex = avatar.defaultAvatarIndex;
    }
    players.push(meetingPlayer);
  }
  players.sort((a, b) => a.turnOrder - b.turnOrder);

  const votes = new Map<string, string>();
  if (isGhost) {
    // MapSchema iteration preserves insertion order — VoterCluster relies on it
    // for vote-arrival recency ordering. Skip empty targets (un-voted ghosts).
    for (const [voterId, vote] of state.ghostMeetingVotes) {
      if (vote.targetId !== '') votes.set(voterId, vote.targetId);
    }
  }

  const secret: SecretWordTruth = isGhost
    ? { mode: 'length', value: me?.secretWordLength ?? 0 }
    : { mode: 'word', value: me?.secretWord ?? '' };

  return {
    meId: ctx.meId,
    pov: povFromRole(role),
    players,
    ghostIds: isGhost ? [...state.ghostIds] : [],
    votes,
    secret,
    phaseEndsAt: state.phaseEndsAt === 0 ? null : state.phaseEndsAt,
    /* Read straight off the wire — the server sets phaseDurationMs in
       transitionToPhase, the dev builder fabricates it from the preset. No
       client-side config constant, so no drift. Invariant: any real
       GhostMeeting state has phaseDurationMs > 0. */
    durationMs: state.phaseDurationMs,
  };
}
